<template>
  <div class="fg-shell">
    <!-- Masqué sur Résultats : cet écran a son propre état d'erreur dédié
         (évite le doublon bannière + bloc d'erreur). -->
    <ErrorBanner v-if="store.error && step !== BookingStep.Results" :error="store.error" @dismiss="store.error = null" />

    <!-- Écrans inline (dans le conteneur hôte) : Search (entrée compacte) et
         Confirmation (écran de redirection bref). -->
    <Transition name="fg-step" mode="out-in">
      <component :is="SCREENS[step]" v-if="!isOverlay" :key="step" />
    </Transition>

    <!-- Funnel immersif : Résultats → Options → Booking, téléporté plein écran
         pour s'affranchir du conteneur hôte (largeur, overflow, backdrop-filter). -->
    <Teleport to="body">
      <Transition name="fg-overlay">
        <div v-if="isOverlay" ref="overlayRef" class="fg-overlay">
          <div class="fg-overlay__panel">
            <Transition name="fg-step" mode="out-in">
              <component :is="SCREENS[step]" :key="step" />
            </Transition>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from '../store'
import { useRouter } from '../router'
import { useTracker } from '../tracker'
import { lockScroll, unlockScroll } from '../utils/scroll-lock'
import { BookingStep } from '../../../shared/types/enums'
import Search from './Search.vue'
import Results from './Results.vue'
import Options from './Options.vue'
import Booking from './Booking.vue'
import Confirmation from './Confirmation.vue'
import ErrorBanner from './atoms/ErrorBanner.vue'

const SCREENS = {
  [BookingStep.Search]: Search,
  [BookingStep.Results]: Results,
  [BookingStep.Options]: Options,
  [BookingStep.Booking]: Booking,
  [BookingStep.Confirmation]: Confirmation,
} as const

// Étapes rendues dans l'overlay plein écran (le funnel qui a besoin de largeur).
// Search reste inline (box d'entrée intégrable partout) ; tout le reste passe
// en overlay plein écran (Confirmation incluse — styles scopés sous .fg-confirm
// dans styles.css, donc téléportables hors shadow DOM).
const OVERLAY_STEPS: BookingStep[] = [
  BookingStep.Results,
  BookingStep.Options,
  BookingStep.Booking,
  BookingStep.Confirmation,
]

const store = useStore()
const router = useRouter()
const tracker = useTracker()

const step = computed(() => router.currentStep.value)
const isOverlay = computed(() => OVERLAY_STEPS.includes(step.value))

// Jalon funnel : toute erreur du tunnel remonte ici via store.error (source unique).
// `code` couvre notamment SEARCH_FAILED, BOOKING_FAILED, JOURNEYS_LOAD_FAILED,
// JOURNEY_DETAIL_FAILED, BRANDS_LOAD_FAILED, MODELS_LOAD_FAILED (+ CATALOG_LOAD_FAILED
// émis directement depuis entry.ts pour les pré-chargements). `current_step` situe l'étape.
watch(() => store.error, (err) => {
  if (!err) return
  tracker.emit('widget_error', {
    code: err.code,
    message: err.message,
    current_step: step.value,
  })
})

// À chaque changement d'étape, on ramène l'overlay (son propre scroll) en haut :
// sinon l'utilisateur arrive au milieu du formulaire (position de scroll héritée
// de l'étape précédente) au lieu du début.
const overlayRef = ref<HTMLElement | null>(null)
watch(step, () => {
  nextTick(() => {
    overlayRef.value?.scrollTo({ top: 0, behavior: 'auto' })
  })
})

// L'overlay funnel est plein écran : on verrouille le scroll de la page hôte
// tant qu'il est ouvert (sinon la page derrière continue de défiler).
let locked = false
watch(isOverlay, (overlay) => {
  if (overlay && !locked) { lockScroll(); locked = true }
  else if (!overlay && locked) { unlockScroll(); locked = false }
}, { immediate: true })
onBeforeUnmount(() => { if (locked) { unlockScroll(); locked = false } })
</script>
